import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Photo } from '@capacitor/camera';
import { CameraAltOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { Alert, Backdrop, Box, Button, IconButton, Stack, SxProps, Theme } from '@mui/material';
import { Webcam } from '@zenput/feature-zencam';
import { useSnackbar } from 'notistack';
import { makeStyles } from 'tss-react/mui';
import { analyzeImageQuality } from '../../../backbone/features/mobileForm/imageField/imageQualityChecker';
import { ZenputFiles } from '../../../backbone/shared/models/files';
import {
    collectCameraNative,
    collectMediaNative,
    collectMediaWeb,
    getZenputFileFromPhoto,
    getZenputFilesFromPhotos
} from '../../../backbone-unorganized/followUpActions/models';
import { createUUID } from '../../../backbone-unorganized/utils/files';
import { MIME_TYPE_TO_EXTENSION } from '../../../constants';
import { isWeb } from '../../../shared/utils/is';
import { confirmNotification } from '../../../shared/utils/notifications';
import { ImageItem } from '../../shared/ImageItem';
import { ImageVideoPickerDialog } from '../../shared/ImageVideoPickerDialog';
import { SimpleGallery } from '../../shared/SimpleGallery/SimpleGallery';
import {
    FollowUpActionFileType,
    FollowUpActionImageType,
    WebMediaResponseType,
    ZenputFileCollection,
    ZenputFileModel
} from './FollowUpActions.types';

interface FollowUpActionsGalleryItemProps {
    alt: string;
    onClick: () => void;
    showError?: boolean;
    src: string;
    showDelete?: boolean;
    onDelete?: () => void;
}

export const FollowUpActionsGalleryItem = ({
    alt,
    onClick,
    src,
    showError = false,
    showDelete = false,
    onDelete = () => {}
}: FollowUpActionsGalleryItemProps) => (
    <Box
        sx={(theme) => ({
            width: '100%',
            aspectRatio: '1 / 1',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: theme.spacing(0.5)
        })}
    >
        <ImageItem
            alt={alt}
            onClick={onClick}
            src={src}
            sx={(theme) => ({
                borderRadius: theme.spacing(0.5),
                boxSizing: 'border-box',
                height: '100%',
                objectFit: 'cover',
                width: '100%',
                ...(showError ? { border: '3px solid #dd2850' } : {})
            })}
        />
        {showDelete && (
            <IconButton
                size="small"
                onClick={onDelete}
                sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'red',
                    color: 'white',
                    '&:hover': { bgcolor: 'darkred' }
                }}
            >
                <DeleteOutlineOutlined fontSize="small" />
            </IconButton>
        )}
    </Box>
);

interface FollowUpActionsGalleryContainerProps {
    children: React.ReactNode;
    sx?: SxProps<Theme>;
}

export const FollowUpActionsGalleryContainer = ({ children, sx }: FollowUpActionsGalleryContainerProps) => {
    return (
        <Box
            sx={[
                (theme) => ({
                    display: 'grid',
                    gap: theme.spacing(1),
                    gridTemplateColumns: `repeat(auto-fill, minmax(${theme.spacing(14)}, 1fr))`,

                    [theme.breakpoints.down('sm')]: {
                        '@media (orientation: portrait)': {
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            justifyContent: 'start'
                        }
                    }
                }),
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
        >
            {children}
        </Box>
    );
};

const useStyles = makeStyles()((theme) => {
    const { spacing } = theme;

    return {
        imageButton: {
            border: 'dashed',
            borderWidth: 'thin',
            color: 'gray',
            padding: spacing(1),
            textTransform: 'none',
            width: '-webkit-fill-available'
        },
        imageButtonReply: {
            border: '.5px solid lightgray',
            color: 'gray',
            marginBottom: spacing(2),
            padding: spacing(1),
            textTransform: 'none',
            width: '-webkit-fill-available'
        },
        boxImageContainer: {
            height: spacing(14),
            width: spacing(14)
        }
    };
});

interface FollowUpActionsGalleryProps {
    getS3URL: (value: string) => string;
    isReply: boolean;
    onDeleteSavedImage?: (index: number) => void;
    onDeleteUploadedImage: (index: number) => void;
    onLoadingChange: Dispatch<SetStateAction<boolean>>;
    onUploadFiles: (images: FollowUpActionFileType[]) => void;
    photoQualityAnalysis?: boolean;
    readOnly?: boolean;
    savedFiles?: FollowUpActionImageType[];
    uploadedFiles: FollowUpActionFileType[];
    uploadFilesToS3: (files: ZenputFileCollection) => Promise<(string | null)[]>;
}

export const FollowUpActionsGallery = (props: FollowUpActionsGalleryProps) => {
    const {
        getS3URL,
        isReply,
        onDeleteSavedImage,
        onDeleteUploadedImage,
        onLoadingChange,
        onUploadFiles,
        photoQualityAnalysis = false,
        readOnly = false,
        savedFiles = [],
        uploadedFiles,
        uploadFilesToS3
    } = props;
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [galleryOpen, toggleGallery] = useState(false);
    const [openCamera, toggleOpenCamera] = useState(false);
    const [showImageDialog, setShowImageDialog] = useState(false);
    const [startIndex, setStartIndex] = useState(0);

    const followUpActionImages = useMemo(
        () => [
            ...savedFiles,
            ...uploadedFiles.map(
                (uploadedFile) =>
                    ({
                        s3_key: uploadedFile.s3_key,
                        is_low_quality: uploadedFile.is_low_quality,
                        laplacian_variance: uploadedFile.laplacian_variance
                    }) as FollowUpActionImageType
            )
        ],
        [savedFiles, uploadedFiles]
    );

    const hasLowQualityImages = useMemo(
        () => followUpActionImages.some((image) => image?.is_low_quality),
        [followUpActionImages]
    );

    const savePhotoFromCameraNative = async (zenputFile: ZenputFileModel, photo: Photo) => {
        try {
            onLoadingChange(true);
            const s3Keys = await uploadFilesToS3(new ZenputFiles([zenputFile]));

            if (!s3Keys[0]) return;

            const fileToUpload = {
                is_low_quality: zenputFile.get('is_low_quality'),
                laplacian_variance: zenputFile.get('laplacian_variance'),
                name: zenputFile.get('name'),
                raw_file: photo.webPath,
                s3_key: s3Keys[0],
                size: zenputFile.get('size'),
                type: zenputFile.get('type')
            } as FollowUpActionFileType;

            onUploadFiles([fileToUpload]);
        } catch (error) {
            enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                variant: 'error'
            });
        } finally {
            onLoadingChange(false);
        }
    };

    const handleToggleGallery = (index: number) => {
        setStartIndex(index);
        toggleGallery(true);
    };

    const handleOnSelect = async (option?: string) => {
        setShowImageDialog(false);
        if (!option) return;

        if (option === 'browse') {
            if (isWeb()) {
                const media: WebMediaResponseType = await collectMediaWeb(photoQualityAnalysis);
                try {
                    onLoadingChange(true);
                    const s3Keys = await uploadFilesToS3(media.files);

                    const filesToSave = s3Keys
                        .map((s3Key, index) =>
                            s3Key ? ({ ...media.displayUrls[index], s3_key: s3Key } as FollowUpActionFileType) : null
                        )
                        .filter((fileToSave): fileToSave is FollowUpActionFileType => fileToSave !== null);

                    if (filesToSave.length > 0) {
                        onUploadFiles(filesToSave);
                    }
                } catch (error) {
                    enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                        variant: 'error'
                    });
                } finally {
                    onLoadingChange(false);
                }
            } else {
                const photos = await collectMediaNative();
                const files = await getZenputFilesFromPhotos(photos, photoQualityAnalysis);
                const filePhotoPairs = files.map((file: ZenputFileModel, index: number) => ({
                    file,
                    photo: photos[index]
                }));

                try {
                    onLoadingChange(true);
                    const s3Keys = await uploadFilesToS3(files);

                    const filesToSave = s3Keys
                        .map((s3Key, index) => {
                            if (!s3Key) return null;
                            const { file, photo } = filePhotoPairs[index];
                            return {
                                is_low_quality: file.get('is_low_quality'),
                                laplacian_variance: file.get('laplacian_variance'),
                                name: file.get('name'),
                                raw_file: photo.webPath,
                                s3_key: s3Key,
                                size: file.get('size'),
                                type: file.get('type')
                            } as FollowUpActionFileType;
                        })
                        .filter((entry): entry is FollowUpActionFileType => entry !== null);

                    if (filesToSave.length > 0) {
                        onUploadFiles(filesToSave);
                    }
                } catch (error) {
                    enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                        variant: 'error'
                    });
                } finally {
                    onLoadingChange(false);
                }
            }
        }

        if (option === 'camera') {
            if (isWeb()) {
                toggleOpenCamera(true);
            } else {
                const photo = await collectCameraNative();
                const zenputFile = await getZenputFileFromPhoto(photo, photoQualityAnalysis);
                if (photoQualityAnalysis && zenputFile.get('is_low_quality')) {
                    confirmNotification(
                        _t('mobile_photo_quality_dialog_message'),
                        async (retakePhoto) => {
                            if (retakePhoto) {
                                await handleOnSelect('camera');
                            } else {
                                await savePhotoFromCameraNative(zenputFile, photo);
                            }
                        },
                        _t('mobile_photo_quality_warning_header'),
                        _t('mobile_take_new_photo'),
                        _t('mobile_use_this_photo')
                    );
                    return;
                }

                await savePhotoFromCameraNative(zenputFile, photo);
            }
        }
    };

    return (
        <Stack>
            {!isReply && !readOnly && (
                <Button
                    className={classes.imageButton}
                    component="label"
                    onClick={() => setShowImageDialog(true)}
                    startIcon={<CameraAltOutlined />}
                >
                    {_t('mobile_field_image_button_add')}
                </Button>
            )}

            {galleryOpen && (
                <SimpleGallery
                    items={[
                        ...savedFiles.map((image, index) => {
                            const src = getS3URL(image.s3_key!);
                            return { id: `${index}-${src}`, src };
                        }),
                        ...uploadedFiles.map((image, index) => ({
                            id: `${savedFiles.length + index}-${image.raw_file}`,
                            src: image.raw_file
                        }))
                    ]}
                    onClose={() => toggleGallery(false)}
                    startIndex={startIndex}
                />
            )}
            {(savedFiles.length > 0 || uploadedFiles.length > 0) && (
                <FollowUpActionsGalleryContainer
                    sx={{
                        paddingBottom: (theme) => theme.spacing(1),
                        paddingTop: (theme) => theme.spacing(2),
                        ...(isReply ? { borderTop: '.5px solid lightgray' } : {})
                    }}
                >
                    {savedFiles.map((image, index) => (
                        <FollowUpActionsGalleryItem
                            alt={`preview ${index}`}
                            key={index}
                            onClick={() => handleToggleGallery(index)}
                            onDelete={() => {
                                if (!readOnly && onDeleteSavedImage) {
                                    onDeleteSavedImage(index);
                                }
                            }}
                            showDelete={!readOnly}
                            src={getS3URL(image.s3_key!)}
                            {...(!readOnly ? { showError: !!image?.is_low_quality } : {})}
                        />
                    ))}

                    {uploadedFiles.map((image, index) => (
                        <FollowUpActionsGalleryItem
                            alt={`preview ${index + savedFiles.length}`}
                            key={index + savedFiles.length}
                            onClick={() => handleToggleGallery(index + savedFiles.length)}
                            onDelete={() => onDeleteUploadedImage(index)}
                            showDelete={!readOnly}
                            src={image.raw_file}
                            {...(!readOnly ? { showError: !!image?.is_low_quality } : {})}
                        />
                    ))}
                </FollowUpActionsGalleryContainer>
            )}

            {hasLowQualityImages && !readOnly && (
                <Alert severity="error" sx={{ marginY: (theme) => theme.spacing(1) }}>
                    {_t('mobile_field_image_low_quality_warning')}
                </Alert>
            )}

            {isReply && !readOnly && (
                <Button
                    className={classes.imageButtonReply}
                    component="label"
                    onClick={() => setShowImageDialog(true)}
                    startIcon={<CameraAltOutlined />}
                >
                    {_t('mobile_field_image_button_add')}
                </Button>
            )}

            {showImageDialog && (
                <ImageVideoPickerDialog
                    onSelect={handleOnSelect}
                    title={_t('mobile_field_image_picker_dialog_title')}
                />
            )}

            {openCamera && (
                <Backdrop open sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                    <Webcam
                        mode="photo"
                        onCapture={async (blob: Blob) => {
                            const extension = blob.type.split('/')[1] || 'jpg';
                            const fileName = `capture_${Date.now()}.${extension}`;

                            const file = new File([blob], fileName, { type: blob.type });

                            const previewUrl = URL.createObjectURL(file);
                            let imageQualityAnalysisResult;
                            if (photoQualityAnalysis) {
                                imageQualityAnalysisResult = await analyzeImageQuality(previewUrl);
                            }

                            const fileToUpload = {
                                is_low_quality: imageQualityAnalysisResult?.isBad ?? null,
                                laplacian_variance: imageQualityAnalysisResult?.laplacianVariance ?? null,
                                name: file.name,
                                raw_file: previewUrl,
                                size: file.size,
                                type: file.type,
                                s3_key: null
                            } as FollowUpActionFileType;

                            const uniqueFileId = createUUID();
                            const extensionFile =
                                MIME_TYPE_TO_EXTENSION[file.type as keyof typeof MIME_TYPE_TO_EXTENSION] ?? 'jpg';
                            const zenputFiles = new ZenputFiles([
                                {
                                    id: uniqueFileId,
                                    mimetype: extensionFile,
                                    name: file.name,
                                    raw_file: file,
                                    size: file.size,
                                    url_base64: previewUrl,
                                    laplacian_variance: imageQualityAnalysisResult?.laplacianVariance,
                                    is_low_quality: imageQualityAnalysisResult?.isBad
                                }
                            ]);

                            try {
                                onLoadingChange(true);
                                const s3Keys = await uploadFilesToS3(zenputFiles);
                                if (s3Keys[0]) {
                                    fileToUpload.s3_key = s3Keys[0];
                                    onUploadFiles([fileToUpload]);
                                }
                            } catch (error) {
                                enqueueSnackbar(_t('mobile_dialog_image_preview_message'), {
                                    variant: 'error'
                                });
                            } finally {
                                onLoadingChange(false);
                            }

                            toggleOpenCamera(false);
                        }}
                        onGoBack={() => {
                            toggleOpenCamera(false);
                        }}
                    />
                </Backdrop>
            )}
        </Stack>
    );
};
